/**
 * Security utility functions for the frontend
 * Provides safe handling of URLs, JSON parsing, base64 encoding, and object operations
 */

// List of allowed URL protocols for external navigation
const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

// List of trusted domains for external navigation (add your trusted domains here)
const TRUSTED_DOMAINS = [
  // TalentLMS domains
  'talentlms.com',
  // Add other trusted external domains as needed
];

/**
 * Validates if a URL is safe to open
 * Prevents javascript: protocol and other dangerous URLs
 * @param url - The URL to validate
 * @returns boolean indicating if the URL is safe
 */
export function isValidUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const parsed = new URL(url);
    return ALLOWED_PROTOCOLS.includes(parsed.protocol);
  } catch {
    // Relative URLs are considered safe
    if (url.startsWith('/') && !url.startsWith('//')) {
      return true;
    }
    return false;
  }
}

/**
 * Validates if a URL belongs to a trusted domain
 * @param url - The URL to validate
 * @returns boolean indicating if the URL is from a trusted domain
 */
export function isTrustedDomain(url: string): boolean {
  if (!isValidUrl(url)) {
    return false;
  }

  try {
    const parsed = new URL(url);
    return TRUSTED_DOMAINS.some(domain =>
      parsed.hostname === domain || parsed.hostname.endsWith(`.${domain}`)
    );
  } catch {
    return false;
  }
}

/**
 * Safely opens a URL in a new tab/window
 * Only opens if the URL passes validation
 * @param url - The URL to open
 * @param options - Optional window features
 * @returns Window object or null if URL is invalid
 */
export function safeWindowOpen(
  url: string | null | undefined,
  options: { noopener?: boolean; noreferrer?: boolean } = {}
): Window | null {
  if (!isValidUrl(url)) {
    return null;
  }

  const features: string[] = [];

  // Default security: always use noopener and noreferrer for external URLs
  if (options.noopener !== false) {
    features.push('noopener');
  }
  if (options.noreferrer !== false) {
    features.push('noreferrer');
  }

  return window.open(url!, '_blank', features.join(','));
}

/**
 * Safely parses JSON with validation and error handling
 * Prevents prototype pollution by removing dangerous keys
 * @param data - The JSON string to parse
 * @param fallback - Fallback value if parsing fails
 * @returns Parsed object or fallback value
 */
export function safeJsonParse<T>(data: string | null | undefined, fallback: T): T {
  if (!data || typeof data !== 'string') {
    return fallback;
  }

  try {
    const parsed = JSON.parse(data);

    // Remove potentially dangerous prototype pollution keys
    if (parsed && typeof parsed === 'object') {
      return sanitizeObject(parsed) as T;
    }

    return parsed as T;
  } catch (error: unknown) {
    return fallback;
  }
}

/**
 * Recursively sanitizes an object to prevent prototype pollution
 * Removes __proto__, constructor, and prototype properties
 * @param obj - Object to sanitize
 * @returns Sanitized object
 */
export function sanitizeObject<T extends object>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item =>
      typeof item === 'object' && item !== null ? sanitizeObject(item) : item
    ) as unknown as T;
  }

  const sanitized: Record<string, unknown> = {};
  const dangerousKeys = ['__proto__', 'constructor', 'prototype'];

  for (const key of Object.keys(obj)) {
    if (dangerousKeys.includes(key)) {
      continue; // Skip dangerous keys
    }

    const value = (obj as Record<string, unknown>)[key];
    if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeObject(value as object);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized as T;
}

/**
 * Safely assigns properties from source objects to target
 * Prevents prototype pollution by excluding dangerous keys
 * @param target - Target object
 * @param sources - Source objects
 * @returns Modified target object
 */
export function safeObjectAssign<T extends object>(
  target: T,
  ...sources: Partial<T>[]
): T {
  const dangerousKeys = ['__proto__', 'constructor', 'prototype'];

  for (const source of sources) {
    if (source && typeof source === 'object') {
      for (const key of Object.keys(source)) {
        if (!dangerousKeys.includes(key)) {
          (target as Record<string, unknown>)[key] = (source as Record<string, unknown>)[key];
        }
      }
    }
  }

  return target;
}

/**
 * Safely decodes a base64 string
 * @param base64String - The base64 string to decode
 * @returns Decoded string or null if invalid
 */
export function safeAtob(base64String: string | null | undefined): string | null {
  if (!base64String || typeof base64String !== 'string') {
    return null;
  }

  // Validate base64 format (basic check)
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;

  // Remove any data URL prefix
  let cleanBase64 = base64String;
  if (base64String.includes(',')) {
    cleanBase64 = base64String.split(',')[1] || '';
  }

  // Check if it's valid base64 format
  if (!base64Regex.test(cleanBase64)) {
    return null;
  }

  try {
    return atob(cleanBase64);
  } catch (error: unknown) {
    return null;
  }
}

/**
 * Safely encodes a string to base64
 * @param str - The string to encode
 * @returns Encoded base64 string or null if invalid
 */
export function safeBtoa(str: string | null | undefined): string | null {
  if (!str || typeof str !== 'string') {
    return null;
  }

  try {
    return btoa(str);
  } catch (error: unknown) {
    return null;
  }
}

/**
 * Validates a MongoDB ObjectId format
 * @param id - The ID to validate
 * @returns boolean indicating if the ID is valid
 */
export function isValidMongoId(id: string | null | undefined): boolean {
  if (!id || typeof id !== 'string') {
    return false;
  }

  return /^[a-fA-F0-9]{24}$/.test(id);
}

/**
 * Sanitizes a string for safe display (basic XSS prevention)
 * For HTML content, use DOMPurify instead
 * @param str - The string to sanitize
 * @returns Sanitized string
 */
export function escapeHtml(str: string | null | undefined): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  const htmlEscapes: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };

  return str.replace(/[&<>"']/g, char => htmlEscapes[char] || char);
}

const securityUtils = {
  isValidUrl,
  isTrustedDomain,
  safeWindowOpen,
  safeJsonParse,
  sanitizeObject,
  safeObjectAssign,
  safeAtob,
  safeBtoa,
  isValidMongoId,
  escapeHtml,
};

export default securityUtils;
